import { useEffect } from 'react';
import {
  SearchModalProvider,
  SidebarSearchModal,
  useSearchModal,
} from '@backstage/plugin-search';
import SearchIcon from '@material-ui/icons/Search';

/*
 * Search-first navigation. With ~20 destinations in the rail, a palette beats
 * hunting through nav groups, so this sits at the top of the sidebar and binds
 * the two shortcuts developers already expect from every other tool: Cmd/Ctrl-K
 * and bare "/".
 *
 * Two traps in plugin-search's own JSDoc example, both hit on the way here:
 *
 * 1. It composes this with a `KeyboardShortcutSearchToggler`, which the package
 *    does not actually export — hence the local implementation below. It has to
 *    render INSIDE SearchModalProvider to share modal state via useSearchModal.
 *
 * 2. It passes a `{({ toggleModal }) => <SearchModal .../>}` render-prop child.
 *    That is for composing SearchModal standalone. SidebarSearchModal ALREADY
 *    renders its own SearchModal internally, and `children` REPLACES that
 *    modal's content — so passing it nests a Dialog inside a Dialog and nothing
 *    opens, from either the click or the shortcut. Leave it childless.
 */

const EDITABLE_TAGS = ['INPUT', 'TEXTAREA', 'SELECT'];

const KeyboardShortcut = () => {
  const { toggleModal } = useSearchModal();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        toggleModal();
        return;
      }

      if (event.key !== '/') {
        return;
      }

      // Without this guard, typing "/" into any field would steal focus.
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (EDITABLE_TAGS.includes(target.tagName?.toUpperCase()) ||
          target.isContentEditable)
      ) {
        return;
      }

      event.preventDefault();
      toggleModal();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleModal]);

  return null;
};

export const SearchPalette = () => (
  <SearchModalProvider>
    <KeyboardShortcut />
    <SidebarSearchModal icon={SearchIcon} />
  </SearchModalProvider>
);
